import { BalanceResponse } from './balance.types';

export interface PlanFeatures {
  allow_multiple_queued_searches: boolean;
  max_queued_searches: number;
  allow_topups: boolean;
  priority_support: boolean;
  dedicated_support: boolean;
  email_discovery: boolean;
  email_verification: boolean;
  confidence_badges: boolean;
  dm_unlock: boolean;
  smart_filters: boolean;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  display_name: string;
  daily_searches: number;
  max_results: number;
  monthly_dm_unlocks: number;
  monthly_email_verifications: number;
  dm_unlock_price_cents: number;
  email_verification_price_cents: number;
  price_cents: number;
  price_display: string;
  trial_fee_cents: number | null;
  trial_fee_display: string | null;
  billing_interval: string | null;
  badge_type: 'MOST_POPULAR' | 'BEST_VALUE' | 'USAGE_BASED' | null;
  features: PlanFeatures;
}

export interface SubscriptionAllowances {
  daily_searches: number;
  max_results: number;
  max_queued_searches: number;
}

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled';

export interface CurrentSubscription {
  id: number;
  plan_name: string;
  plan_display_name: string;
  status: SubscriptionStatus;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  is_trial: boolean;
  features: PlanFeatures;
  allowances: SubscriptionAllowances;
  /** Not currently populated by GET /subscriptions/current; kept optional for existing dunning-retry UI. */
  next_payment_attempt?: number | null;
}

export interface CurrentSubscriptionResponse {
  subscription: CurrentSubscription | null;
  balance: BalanceResponse | null;
  trial_eligible: boolean;
}

export interface PlansResponse {
  plans: SubscriptionPlan[];
}

export interface CheckoutSessionResponse {
  checkout_url: string;
  session_id: string;
  with_trial: boolean;
}

export interface PortalSessionResponse {
  portal_url: string;
}

export type PlanSwitchDirection = 'upgrade' | 'downgrade';

export interface ChangePlanResponse {
  subscription: CurrentSubscription;
  direction: PlanSwitchDirection;
}

/**
 * Cardless invite trials report status "active" with is_trial true;
 * only card-based Stripe trials report status "trialing".
 */
export function isInTrial(subscription: CurrentSubscription | null | undefined): boolean {
  if (!subscription) return false;
  return subscription.is_trial === true || subscription.status === 'trialing';
}

export function trialDaysLeft(currentPeriodEnd: string | null | undefined): number | null {
  if (!currentPeriodEnd) return null;
  const end = new Date(currentPeriodEnd);
  const diffMs = end.getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}
